import { API_BASE, APP_NAME } from '../config';
import { NetworkError, checkApiKey, setApiKey } from '../data/api';
import { kickSync } from '../data/sync';
import { btn, field, input } from '../ui/components';
import { h } from '../ui/dom';
import { navigate, takeNext } from '../ui/router';
import type { View } from '../ui/router';
import { shell } from '../app';

export const loginView: View = ({ root }) => {
  shell.setChrome(false);
  document.title = `Sign in · ${APP_NAME}`;
  let key = '';
  const error = h('p', { class: 'form-error', role: 'alert', hidden: true });
  const offlineBtn = btn('Use this key offline for now', {
    variant: 'subtle',
    size: 'sm',
    onClick: () => {
      if (!key.trim()) return;
      setApiKey(key.trim());
      navigate(takeNext() ?? '#/home', { replace: true });
    },
  });
  offlineBtn.hidden = true;
  const submit = btn('Connect', { variant: 'primary', size: 'lg', type: 'submit', class: 'w-full' });
  const keyInput = input({
    type: 'password',
    placeholder: 'Paste your API key',
    autocomplete: 'current-password',
    enterkeyhint: 'go',
    onInput: (v) => {
      key = v;
      error.hidden = true;
    },
  });
  const form = h(
    'form',
    {
      class: 'login__form',
      onSubmit: async (ev: Event) => {
        ev.preventDefault();
        const k = key.trim();
        if (!k) return;
        submit.disabled = true;
        submit.textContent = 'Checking…';
        try {
          const ok = await checkApiKey(k);
          if (!ok) {
            error.textContent = 'That key was rejected. Check it and try again.';
            error.hidden = false;
            return;
          }
          setApiKey(k);
          kickSync(0);
          navigate(takeNext() ?? '#/home', { replace: true });
        } catch (err) {
          error.textContent =
            err instanceof NetworkError
              ? "Can't reach the API right now. Check your signal, or keep working offline."
              : `Unexpected error: ${err instanceof Error ? err.message : String(err)}`;
          error.hidden = false;
          offlineBtn.hidden = !(err instanceof NetworkError);
        } finally {
          submit.disabled = false;
          submit.textContent = 'Connect';
        }
      },
    },
    field('API key', keyInput),
    submit,
    error,
    offlineBtn,
  );
  root.appendChild(
    h(
      'div',
      { class: 'login' },
      h(
        'div',
        { class: 'login__brand' },
        h('div', { class: 'logo', 'aria-hidden': 'true' }, h('span', { class: 'logo__mark' }, 'OC')),
        h('h1', { class: 'login__title' }, APP_NAME),
        h('p', { class: 'login__tagline' }, 'Field notes. Offline first.'),
      ),
      form,
      h('p', { class: 'login__meta mono' }, `API · ${API_BASE || 'same origin'}`),
    ),
  );
  setTimeout(() => keyInput.focus(), 50);
};
